"use client";

import dynamic from "next/dynamic";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";

/**
 * Page prototypes — assemblies, not components. PGE-001 strings the board's
 * strongest real material into one scrolling story a cold reader can follow;
 * PGE-002 stages a single governed turn as choreography. Both speak the plain
 * register from TON-017 and the settled surface language: light sections on
 * the canonical ground, dark product assets floating inside them.
 *
 * The production pages import this module's furniture (BandRail, CtaPair), so
 * the two exhibits live in page-prototypes.tsx, on the application's own
 * views, and load only where the board renders them.
 */
import {
  markdownRehypePlugins,
  markdownRemarkPlugins,
} from "@product/components/content/markdown-math";
import { normalizeMarkdownContent } from "@product/components/content/markdown-normalize";
import { staticMarkdownRendererComponents } from "@product/components/content/markdown-renderer-components-static";
import { useStreamingMarkdownRehypePlugins } from "@product/components/app/runs/assistant-response-streaming";

import { DISPLAY, EDITORIAL_ACTION_CLS, LABEL_CLS } from "@/components/editorial";

import type { ConceptGroup } from "../spec";

const StoryPage = dynamic(() => import("./page-prototypes").then((module) => module.StoryPage));
const LivingSpecimen = dynamic(() => import("./page-prototypes").then((module) => module.LivingSpecimen));

const INK_72 = "text-[color-mix(in_srgb,var(--theme-ink)_72%,transparent)]";
const INK_80 = "text-[color-mix(in_srgb,var(--theme-ink)_80%,transparent)]";
const INK_88 = "text-[color-mix(in_srgb,var(--theme-ink)_88%,transparent)]";
const GINK_82 = "text-[color-mix(in_srgb,var(--theme-ground)_82%,transparent)]";

/* ================================================================= */
/*  Shared page furniture                                             */
/* ================================================================= */

/** The governed answer through the real pipeline, condensed. */
export function CondensedAnswer({ children }: { children: string }) {
  return (
    <div className="typeset typeset-chat max-w-[58ch] break-words whitespace-normal">
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={markdownRehypePlugins}
        components={staticMarkdownRendererComponents}
      >
        {normalizeMarkdownContent(children)}
      </ReactMarkdown>
    </div>
  );
}

export function QuestionBubble({ children }: { children: string }) {
  return (
    <div className="flex justify-end">
      <p
        className="m-0 w-fit max-w-[90%] text-sm leading-relaxed break-words"
        style={{
          background: "hsl(var(--card))",
          color: "hsl(var(--foreground))",
          borderRadius: "18px 18px 4px 18px",
          padding: "8px 14px",
        }}
      >
        {children}
      </p>
    </div>
  );
}

/** Editorial CTA pair — real links (cross-page anchors work everywhere). */
export function CtaPair({ onDark = false }: { onDark?: boolean }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <a
        href="#waitlist"
        className={`${EDITORIAL_ACTION_CLS} ${
          onDark
            ? "bg-[var(--theme-ground)] text-[var(--theme-ink)]"
            : "bg-[var(--theme-dark)] text-[var(--theme-ground)]"
        }`}
      >
        Discuss your first workflow
      </a>
      <a
        href="/#how-it-works"
        className={`${EDITORIAL_ACTION_CLS} border ${
          onDark
            ? `border-[color-mix(in_srgb,var(--theme-ground)_36%,transparent)] ${GINK_82}`
            : `border-[var(--rule)] ${INK_80}`
        }`}
      >
        How it works
      </a>
    </div>
  );
}

/** A band's left rail: number, head, plain-words body. */
export function BandRail({
  index,
  head,
  children,
  headingLevel = "h3",
}: {
  index?: string;
  /** Rich content allows hand-drawn marks on a word (concept SKT). */
  head: ReactNode;
  children: ReactNode;
  headingLevel?: "h2" | "h3";
}) {
  const Heading = headingLevel;
  return (
    <div className="max-w-[38rem]">
      {index ? <p className={`${LABEL_CLS} text-[var(--theme-accent-text)]`}>{index}</p> : null}
      <Heading
        style={DISPLAY}
        className={`${INK_88} ${index ? "mt-3" : ""} max-w-[22ch] text-[1.5rem] leading-[1.16] font-semibold tracking-[-0.02em] sm:text-[1.9rem]`}
      >
        {head}
      </Heading>
      <p className={`mt-3 max-w-[52ch] ${INK_72} text-[0.95rem] leading-[1.65]`}>
        {children}
      </p>
    </div>
  );
}

/* ================================================================= */
/*  PGE-001 · The story page                                          */
/* ================================================================= */

/** The five pillars in the plain register, digest form. */
export const PILLAR_DIGEST: { plain: string; line: string; ref: string }[] = [
  {
    plain: "Lives with your business",
    line: "Always on, on a server that is yours alone, UK, EU or US.",
    ref: "PIL-001",
  },
  {
    plain: "Meaning, written down",
    line: "It proposes definitions in plain words; every version is kept and inspectable.",
    ref: "PIL-002",
  },
  {
    plain: "Any AI, your choice",
    line: "OpenAI, Anthropic, Google or your own hardware, swap models, keep the meaning.",
    ref: "PIL-003",
  },
  {
    plain: "Files records, not threads",
    line: "Agents must write into tables you define, so knowledge stays findable.",
    ref: "PIL-004",
  },
  {
    plain: "In the tools you already use",
    line: "MLX, Claude, ChatGPT, terminal or code, shared business context, controlled access.",
    ref: "PIL-005",
  },
];

/**
 * The specimen streams through the product's own path: the source grows in
 * slices, normalized with the streaming flag, and the application's
 * useStreamingMarkdownRehypePlugins wraps each new run in the real
 * .mlx-stream-text-run wipe (stream-text.css, imported from the app).
 */
export function StreamingAnswer({
  source,
  streaming,
  identity,
}: {
  source: string;
  streaming: boolean;
  identity: string;
}) {
  const normalized = normalizeMarkdownContent(source, { streaming });
  const streamingRehypePlugins = useStreamingMarkdownRehypePlugins({
    enabled: streaming,
    source: normalized,
    streamIdentity: identity,
  });
  const rehypePlugins = streamingRehypePlugins
    ? [...streamingRehypePlugins, ...markdownRehypePlugins]
    : markdownRehypePlugins;
  return (
    <div
      className="typeset typeset-chat max-w-[58ch] break-words whitespace-normal"
      data-streaming={streaming ? "true" : undefined}
    >
      <ReactMarkdown
        remarkPlugins={markdownRemarkPlugins}
        rehypePlugins={rehypePlugins}
        components={staticMarkdownRendererComponents}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}

export const REVEAL_STEP_CHARS = 48;
export const REVEAL_TICK_MS = 110;

/* ================================================================= */

export const PAGE_GROUP: ConceptGroup = {
  id: "pages",
  prefix: "PGE",
  title: "Page prototypes",
  blurb:
    "Assemblies, not components: the board's real material composed the way a visitor would meet it. The story page walks a cold reader from plain-words claim to governed proof in one scroll; the living specimen stages a single turn so the loop can be watched, not inferred. Both speak the plain register (TON-017) and the settled surface language, light sections, dark product assets, one canonical night.",
  specs: [
    {
      id: "PGE-001",
      title: "The story page",
      note: "One scroll from cold to convinced: plain-words hero, the governed answer as a dark asset, records-not-threads, the five pillars in digest, the refusal as the trust close, and a night CTA band.",
      surface: "ground",
      width: "full",
      interaction: "static",
      state: "One scroll",
      render: () => <StoryPage />,
    },
    {
      id: "PGE-002",
      title: "The living specimen",
      note: "The governed turn staged in four seconds (question, answer, receipt, filed artifact) on the real components, with a Replay control and a settled initial state for print and reduced motion.",
      surface: "ground",
      width: "full",
      interaction: "prototype",
      state: "Staged turn",
      render: () => <LivingSpecimen />,
    },
  ],
};
